import mergeWith from 'lodash/mergeWith';
import { BaseLowCodeProps, SortItem, MetaConfigItem, FieldType } from '../types';
import { shakeObj } from '@/utils';
import { DqiItem } from './components/drag-item/types';
import { FieldsItemProps } from './components/drag-target-container';
import { AggregationType } from './const';

export const validateSingleField = (newItem: DqiItem, records: DqiItem[]) => {
  if (records.length >= 1) {
    return '当前区域最多支持1个字段';
  }

  return null;
};

export const mergeMetaConfig = (
  defaultConfig: Record<string, FieldsItemProps>,
  metaConfig?: Record<string, FieldsItemProps>,
): Record<string, FieldsItemProps> => {
  const mergedConfig = mergeWith(
    defaultConfig,
    metaConfig,
    (objValue: any, srcValue: any, key: string, object: any, source: any, stack: any) => {
      // 只合并到第二层属性
      if (stack.size > 2 || key === 'records') {
        return srcValue;
      }
      if (key === 'title') {
        return objValue;
      }
    },
  );

  return mergedConfig;
};

/**
 * @description 获取聚合配置
 * @param {{ [key:string]: MetaConfigItem }} metaConfig
 * @return {*} {BaseLowCodeProps['aggregationConfig']}
 */
export const getAggregationConfig = (
  metaConfig?: {
    [key: PropertyKey]: MetaConfigItem;
  },
  options?: {
    /** 表格排序字段。因为可以从运行时改变，所以不能从 metaConfig 中获取 */
    tableSorts?: SortItem[];
  },
): BaseLowCodeProps['aggregationConfig'] => {
  if (!metaConfig) {
    return null;
  }

  const metaConfigs = Object.values(metaConfig);
  if (metaConfigs.length === 0) {
    return null;
  }

  const metricsGroup = metaConfigs
    ?.filter((item) => item?.type === FieldType.Measure)
    ?.flatMap((item) =>
      item?.records?.map((record) => ({
        name: record?.name,
        func: record?.settings?.[AggregationType.Metrics],
        alias: record?.name,
      })),
    );
  // 聚合指标。存在 func 聚合函数的字段
  const metrics = metricsGroup.filter((item) => Boolean(item?.func));

  const dimsGroup = metaConfigs
    .filter((item) => item?.type === FieldType.Dims)
    .flatMap((item) => item.records?.map((record) => record.name));
  const metricsWithoutFunc = metricsGroup.filter((item) => !item?.func).map((item) => item?.name);

  // 聚合纬度。来源有2个： 1. 指定的维度组（如柱状图的x轴） 2. 没有指定聚合函数的指标（如表格的维度）
  const dims = dimsGroup.concat(metricsWithoutFunc).filter(Boolean);

  // 排序字段
  const sortsGroup = metaConfigs
    .flatMap((item) => item.records)
    .filter((item) => item?.settings?.[AggregationType.Sorts])
    .map((item) => ({
      name: item?.name || '',
      type: item?.settings?.[AggregationType.Sorts] || '',
    }));

  // 兼容旧的表格排序方式
  const tableSortGroup = (options?.tableSorts || []).map((item) => ({
    name: item?.resultName || '',
    type: item?.order || '',
  }));
  const sorts = sortsGroup.concat(tableSortGroup).filter((item) => Boolean(item.type));

  // 只在有聚合指标配置时返回，否则返回 null
  const hasAggregation = metrics.length > 0;

  const aggregationConfig: BaseLowCodeProps['aggregationConfig'] = {
    aggregation: hasAggregation
      ? {
          metrics,
          dims,
        }
      : undefined,
    sorts: sorts.length > 0 ? sorts : undefined,
  };

  return shakeObj(aggregationConfig);
};
